import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Disclosure, Transition } from '@headlessui/react';
import PageTitle from '../components/ui/PageTitle';
import StoreOfflineBanner from '../components/StoreOfflineBanner';
import useStoreStatus from '../hooks/useStoreStatus';
import { supabase } from '../lib/supabase';

function parseCartFromUrl(raw) {
     if (!raw) return {};
     const map = {};
     raw.split(',').forEach((pair) => {
          const [id, qty] = pair.split(':');
          const n = Number(qty);
          if (id && n > 0) map[id] = n;
     });
     return map;
}

export default function Pricing() {
     const [searchParams] = useSearchParams();
     const [packages, setPackages] = useState([]);
     const [items, setItems] = useState([]);
     const [loading, setLoading] = useState(true);
     const { offline, message, loading: statusLoading } = useStoreStatus();
     const [quantities, setQuantities] = useState(() =>
          parseCartFromUrl(searchParams.get('items'))
     );
     const [lightbox, setLightbox] = useState(null);
     const navigate = useNavigate();

     useEffect(() => {
          fetchEquipment();
     }, []);

     useEffect(() => {
          if (!lightbox) return;
          function onKey(e) {
               if (e.key === 'Escape') setLightbox(null);
          }
          document.addEventListener('keydown', onKey);
          const prevOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          return () => {
               document.removeEventListener('keydown', onKey);
               document.body.style.overflow = prevOverflow;
          };
     }, [lightbox]);

     async function fetchEquipment() {
          setLoading(true);
          const { data } = await supabase
               .from('equipment')
               .select('*')
               .eq('is_active', true)
               .order('daily_rate', { ascending: false });
          const all = data || [];
          const alaCarte = all.filter((e) => !e.is_package);
          setPackages(all.filter((e) => e.is_package));
          setItems(alaCarte);

          // Prune cart entries that aren't à la carte items or exceed stock
          const validIds = new Set(alaCarte.map((e) => e.id));
          setQuantities((prev) => {
               const next = {};
               for (const [id, qty] of Object.entries(prev)) {
                    if (!validIds.has(id)) continue;
                    const max = alaCarte.find((e) => e.id === id)?.quantity || 1;
                    const clamped = Math.min(qty, max);
                    if (clamped > 0) next[id] = clamped;
               }
               return next;
          });
          setLoading(false);
     }

     const pkg = packages[0] || null;

     function setQty(id, maxQty, nextQty) {
          const clamped = Math.max(0, Math.min(maxQty, nextQty));
          setQuantities((prev) => {
               const next = { ...prev };
               if (clamped === 0) delete next[id];
               else next[id] = clamped;
               return next;
          });
     }

     const selectedItems = items
          .map((item) => ({ ...item, qty: quantities[item.id] || 0 }))
          .filter((item) => item.qty > 0);

     const totalDailyCents = selectedItems.reduce(
          (sum, i) => sum + i.daily_rate * i.qty,
          0
     );
     const totalQty = selectedItems.reduce((sum, i) => sum + i.qty, 0);

     function handleContinueToBooking() {
          if (offline || statusLoading) return;
          const pairs = selectedItems.map((i) => `${i.id}:${i.qty}`).join(',');
          navigate(`/schedule?mode=alacarte&items=${encodeURIComponent(pairs)}`);
     }

     function handleBookPackage() {
          if (offline || statusLoading) return;
          if (!pkg) {
               navigate('/schedule');
               return;
          }
          navigate(`/schedule?mode=package&items=${encodeURIComponent(`${pkg.id}:1`)}`);
     }

     return (
          <>
               <PageTitle
                    firstWord='Rental'
                    secondWord='Pricing'
                    breadcrumbLink='Pricing'
               />
               <div className='mx-auto max-w-7xl px-6 py-12 laptop:py-24 laptop:px-8 pb-36'>
                    {offline && <StoreOfflineBanner message={message} />}
                    <CompleteSystemSection
                         pkg={pkg}
                         loading={loading}
                         offline={offline}
                         statusLoading={statusLoading}
                         onImageClick={setLightbox}
                         onBook={handleBookPackage}
                    />

                    <div className='mt-20 laptop:mt-32'>
                         <AlaCarteSection
                              items={items}
                              loading={loading}
                              quantities={quantities}
                              onQtyChange={setQty}
                              onImageClick={setLightbox}
                         />
                    </div>
               </div>

               {/* Sticky summary bar */}
               {totalQty > 0 && (
                    <div className='fixed bottom-0 left-0 right-0 z-40 bg-dark text-white border-t-2 border-yellow shadow-2xl'>
                         <div className='max-w-7xl mx-auto px-6 py-4 flex flex-col tablet:flex-row items-center justify-between gap-4'>
                              <div>
                                   <p className='text-white font-semibold'>
                                        {totalQty} {totalQty === 1 ? 'item' : 'items'} selected
                                   </p>
                                   <p className='text-yellow text-2xl font-bold'>
                                        ${(totalDailyCents / 100).toLocaleString()}
                                        <span className='text-white/60 text-sm font-normal ml-1'>
                                             /day
                                        </span>
                                   </p>
                              </div>
                              <div className='flex items-center gap-3'>
                                   <button
                                        type='button'
                                        onClick={() => setQuantities({})}
                                        className='text-white/60 text-sm hover:text-white duration-300 underline'
                                   >
                                        Clear
                                   </button>
                                   <button
                                        type='button'
                                        onClick={handleContinueToBooking}
                                        disabled={offline || statusLoading}
                                        title={offline ? 'Booking is temporarily closed' : undefined}
                                        className='group relative font-semibold text-base uppercase tracking-wider bg-yellow text-dark pl-10 pr-6 py-4 rounded-xl hover:bg-yellow/90 duration-300 flex items-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-yellow'
                                   >
                                        <span className='text-dark pr-2 absolute left-4 duration-300 group-hover:tracking-[0.15em]'>
                                             {'//'}
                                        </span>
                                        {offline ? 'Booking Closed' : 'Continue to Booking'}
                                   </button>
                              </div>
                         </div>
                    </div>
               )}

               {lightbox && (
                    <div
                         onClick={() => setLightbox(null)}
                         className='fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out'
                         role='dialog'
                         aria-modal='true'
                    >
                         <button
                              type='button'
                              onClick={() => setLightbox(null)}
                              aria-label='Close image'
                              className='absolute top-4 right-4 w-10 h-10 rounded-full border border-white/40 text-white text-xl hover:bg-white/10 duration-300 flex items-center justify-center'
                         >
                              ×
                         </button>
                         <img
                              src={lightbox.src}
                              alt={lightbox.alt}
                              onClick={(e) => e.stopPropagation()}
                              className='max-w-full max-h-full object-contain rounded-lg cursor-default'
                         />
                    </div>
               )}
          </>
     );
}

function PrimaryButton({ onClick, href, disabled, title, children }) {
     const base =
          'group relative font-semibold text-base uppercase tracking-wider bg-yellow text-dark pl-10 pr-6 py-4 rounded-xl hover:bg-yellow/90 duration-300 flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-yellow';
     const inner = (
          <>
               <span className='text-dark pr-2 absolute left-4 duration-300 group-hover:tracking-[0.15em]'>
                    {'//'}
               </span>
               {children}
          </>
     );
     if (href && !disabled) {
          return (
               <a href={href} title={title} className={base}>
                    {inner}
               </a>
          );
     }
     return (
          <button type='button' onClick={onClick} disabled={disabled} title={title} className={base}>
               {inner}
          </button>
     );
}

function CompleteSystemSection({
     pkg,
     loading,
     offline,
     statusLoading,
     onImageClick,
     onBook,
}) {
     // Stay disabled until the store status is known, so the CTA is never live
     // during the fetch.
     const bookingDisabled = offline || statusLoading;
     const priceDisplay = pkg ? `$${(pkg.daily_rate / 100).toLocaleString()}` : null;
     const depositDisplay = pkg && pkg.deposit_amount
          ? `$${(pkg.deposit_amount / 100).toLocaleString()} deposit`
          : null;
     const deliveryDisplay = pkg && pkg.delivery_available
          ? `$${(pkg.delivery_fee / 100).toLocaleString()} delivery`
          : null;

     return (
          <section>
               <div className='flex flex-wrap items-center gap-3 mb-5'>
                    <span className='bg-yellow text-dark text-sm font-bold uppercase tracking-widest px-4 py-2 rounded-full'>
                         Most customers choose this
                    </span>
               </div>

               <div className='laptop:grid laptop:grid-cols-12 laptop:gap-10 laptop:items-center'>
                    {/* Image */}
                    <div className='laptop:col-span-6 mb-8 laptop:mb-0'>
                         {pkg && pkg.image_url ? (
                              <button
                                   type='button'
                                   onClick={() => onImageClick({ src: pkg.image_url, alt: pkg.name })}
                                   className='group w-full aspect-[4/3] rounded-2xl overflow-hidden border border-gray-200 bg-white cursor-zoom-in'
                                   aria-label={`Enlarge image of ${pkg.name}`}
                              >
                                   <img
                                        src={pkg.image_url}
                                        alt={pkg.name}
                                        className='w-full h-full object-contain duration-300 group-hover:scale-105'
                                   />
                              </button>
                         ) : (
                              <div className='w-full aspect-[4/3] rounded-2xl border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-darker/40 text-sm'>
                                   Add a package image in Admin → Inventory
                              </div>
                         )}
                    </div>

                    {/* Copy + price */}
                    <div className='laptop:col-span-6'>
                         <h1 className='text-dark uppercase font-bold text-[36px] laptop:text-[48px] leading-[1.05] mb-4'>
                              Complete Tile Removal
                              <span className='block text-yellow'>System</span>
                         </h1>

                         <p className='text-darker/80 text-base laptop:text-lg mb-6'>
                              Everything you need in one trailer — HEPA vacuums,
                              demolition hammers with dust shrouds, floor grinders, and
                              debris collection. Rip out tile faster, cleaner, and
                              without the 3–4 post-job cleanings traditional demolition
                              leaves behind.
                         </p>

                         <div className='mb-6'>
                              <p className='text-yellow text-[48px] laptop:text-[64px] font-bold leading-none'>
                                   {priceDisplay ? (
                                        <>
                                             {priceDisplay}
                                             <span className='text-darker/50 text-lg font-normal ml-1'>
                                                  /day
                                             </span>
                                        </>
                                   ) : (
                                        <span
                                             className={`inline-block w-40 h-12 laptop:h-16 rounded-lg bg-gray-200 align-middle ${loading ? 'animate-pulse' : ''}`}
                                             aria-label={loading ? 'Loading price' : 'Price unavailable'}
                                        />
                                   )}
                              </p>
                              <p className='text-darker font-semibold text-sm mt-2'>
                                   Typically replaces $1,200+ in labor, cleanup, and equipment rentals.
                              </p>
                              <p className='text-darker/60 text-sm mt-1'>
                                   Full trailer rental
                                   {depositDisplay && (
                                        <span className='ml-2'>· {depositDisplay}</span>
                                   )}
                                   {deliveryDisplay && (
                                        <span className='ml-2'>· {deliveryDisplay}</span>
                                   )}
                              </p>
                         </div>

                         <PrimaryButton
                              onClick={onBook}
                              disabled={bookingDisabled}
                              title={offline ? 'Booking is temporarily closed' : undefined}
                         >
                              {offline ? 'Booking Closed' : 'Book Full System'}
                         </PrimaryButton>

                         <IncludedDisclosure pkg={pkg} />
                    </div>
               </div>

               {/* Feature cards */}
               <div className='grid grid-cols-1 tablet:grid-cols-3 gap-4 mt-12'>
                    <FeatureCard
                         title='Finish in 1 day instead of 2–3'
                         body='Professional-grade tools move through porcelain, ceramic, and thinset at a contractor’s pace.'
                    />
                    <FeatureCard
                         title='No dust spreading through the house'
                         body='HEPA vacuums + dust shrouds keep silica dust out of your cabinets, HVAC, and lungs.'
                    />
                    <FeatureCard
                         title='Everything you need — no extra trips'
                         body='The full kit is pre-packed. You won’t stop mid-job to hunt down a tool you forgot.'
                    />
               </div>

               {/* Traditional vs Dustless comparison */}
               <div className='mt-16 border-t border-gray-200 pt-10'>
                    <h2 className='text-dark uppercase font-bold text-[28px] laptop:text-[32px] leading-tight mb-8 text-center'>
                         Traditional Demo{' '}
                         <span className='text-darker/40'>vs</span>{' '}
                         <span className='text-yellow'>Dustless</span>
                    </h2>

                    <ComparisonTable />
               </div>
          </section>
     );
}

function FeatureCard({ title, body }) {
     return (
          <div className='rounded-xl border border-gray-200 p-5 bg-white'>
               <h3 className='text-dark font-bold text-lg mb-2'>{title}</h3>
               <p className='text-darker/70 text-sm leading-relaxed'>{body}</p>
          </div>
     );
}

function IncludedDisclosure({ pkg }) {
     const description = pkg && pkg.description && pkg.description.trim();
     if (!description) return null;

     return (
          <div className='mt-6'>
               <Disclosure>
                    {({ open }) => (
                         <div className={`rounded-xl border-2 duration-300 ${open ? 'border-yellow bg-yellow/5' : 'border-gray-200 bg-white hover:border-darker/30'}`}>
                              <Disclosure.Button className='flex w-full items-center justify-between px-5 py-4 text-left'>
                                   <span className='text-dark font-bold uppercase tracking-wider text-sm'>
                                        What's included in the full system
                                   </span>
                                   <span className={`text-yellow text-2xl flex-shrink-0 duration-300 ${open ? 'rotate-45' : ''}`}>
                                        +
                                   </span>
                              </Disclosure.Button>
                              <Transition
                                   enter='duration-200 ease-out'
                                   enterFrom='opacity-0 max-h-0'
                                   enterTo='opacity-100 max-h-[800px]'
                                   leave='duration-150 ease-in'
                                   leaveFrom='opacity-100 max-h-[800px]'
                                   leaveTo='opacity-0 max-h-0'
                              >
                                   <Disclosure.Panel className='overflow-hidden'>
                                        <div className='px-5 pb-5 border-t border-yellow/30 pt-4'>
                                             <p className='text-darker/80 text-sm whitespace-pre-line leading-relaxed'>
                                                  {description}
                                             </p>
                                        </div>
                                   </Disclosure.Panel>
                              </Transition>
                         </div>
                    )}
               </Disclosure>
          </div>
     );
}

function ComparisonTable() {
     const rows = [
          { label: 'Dust control', traditional: 'Dust everywhere', dustless: 'Contained at the source' },
          { label: 'Post-job cleanup', traditional: '3–4 professional cleanings', dustless: 'Minimal — no deep clean' },
          { label: 'Timeline', traditional: '2–3 days for an average job', dustless: '1 day possible' },
          { label: 'Silica exposure', traditional: 'Airborne throughout the home', dustless: 'HEPA-captured at the tool' },
     ];

     return (
          <div className='grid grid-cols-2 gap-3 laptop:gap-5 max-w-4xl mx-auto'>
               {/* Headers */}
               <div className='rounded-xl border border-gray-200 bg-gray-50 p-4 text-center'>
                    <p className='text-darker/50 text-xs uppercase tracking-widest font-semibold'>
                         Traditional
                    </p>
               </div>
               <div className='rounded-xl border-2 border-yellow bg-yellow/5 p-4 text-center'>
                    <p className='text-dark text-xs uppercase tracking-widest font-bold'>
                         Dustless (Ripout)
                    </p>
               </div>

               {/* Rows */}
               {rows.map((row) => (
                    <React.Fragment key={row.label}>
                         <div className='rounded-xl border border-gray-200 bg-white p-4 flex items-start gap-3'>
                              <span className='text-red-500 text-xl leading-none mt-0.5 flex-shrink-0'>
                                   ✕
                              </span>
                              <div>
                                   <p className='text-darker/50 text-[11px] uppercase tracking-wider font-semibold mb-1'>
                                        {row.label}
                                   </p>
                                   <p className='text-darker text-sm laptop:text-base'>
                                        {row.traditional}
                                   </p>
                              </div>
                         </div>
                         <div className='rounded-xl border-2 border-yellow bg-white p-4 flex items-start gap-3'>
                              <span className='text-green-600 text-xl leading-none mt-0.5 flex-shrink-0'>
                                   ✓
                              </span>
                              <div>
                                   <p className='text-darker/50 text-[11px] uppercase tracking-wider font-semibold mb-1'>
                                        {row.label}
                                   </p>
                                   <p className='text-darker text-sm laptop:text-base font-semibold'>
                                        {row.dustless}
                                   </p>
                              </div>
                         </div>
                    </React.Fragment>
               ))}
          </div>
     );
}

function AlaCarteSection({ items, loading, quantities, onQtyChange, onImageClick }) {
     return (
          <section>
               <div className='mb-8'>
                    <p className='text-darker/60 text-sm italic mb-3'>
                         Only need specific tools? Build your own setup below.
                    </p>
                    <span className='text-darker/40 text-xs font-bold uppercase tracking-widest'>
                         Also available
                    </span>
                    <h2 className='text-dark uppercase font-bold text-[32px] laptop:text-[40px] leading-[1.1] mt-2'>
                         À La Carte Equipment
                    </h2>
                    <p className='text-darker/70 text-base mt-3 max-w-2xl'>
                         Pick individual tools for smaller jobs or to fill gaps in
                         your own kit. Select items below and continue to booking.
                    </p>
               </div>

               {loading ? (
                    <p className='text-darker/60'>Loading equipment...</p>
               ) : items.length === 0 ? (
                    <p className='text-darker/60'>
                         No individual equipment is available at this time. Please check back soon or book the full package above.
                    </p>
               ) : (
                    <div className='grid grid-cols-1 tablet:grid-cols-2 gap-4'>
                         {items.map((item) => (
                              <AlaCarteCard
                                   key={item.id}
                                   item={item}
                                   qty={quantities[item.id] || 0}
                                   maxQty={item.quantity || 1}
                                   onQtyChange={onQtyChange}
                                   onImageClick={onImageClick}
                              />
                         ))}
                    </div>
               )}
          </section>
     );
}

function AlaCarteCard({ item, qty, maxQty, onQtyChange, onImageClick }) {
     const selected = qty > 0;
     return (
          <div
               className={`rounded-2xl border-2 duration-300 bg-white overflow-hidden flex flex-col ${
                    selected ? 'border-yellow shadow-lg' : 'border-gray-200 hover:border-yellow/50'
               }`}
          >
               {item.image_url && (
                    <button
                         type='button'
                         onClick={() => onImageClick({ src: item.image_url, alt: item.name })}
                         className='group w-full h-52 flex-shrink-0 overflow-hidden cursor-zoom-in relative bg-white border-b border-gray-200'
                         aria-label={`Enlarge image of ${item.name}`}
                    >
                         <img
                              src={item.image_url}
                              alt={item.name}
                              className='w-full h-full object-contain duration-300 group-hover:scale-105'
                         />
                    </button>
               )}

               <div className='p-5 flex-1 flex flex-col'>
                    <div className='flex items-start justify-between gap-4'>
                         <h3 className='text-darker text-lg font-bold'>
                              {item.name}
                         </h3>
                         <p className='text-yellow text-xl font-bold whitespace-nowrap'>
                              ${(item.daily_rate / 100).toLocaleString()}
                              <span className='text-darker/50 text-sm font-normal'>
                                   /day
                              </span>
                         </p>
                    </div>
                    {item.description && (
                         <p className='text-darker/70 text-sm mt-2 whitespace-pre-line leading-relaxed flex-1'>
                              {item.description}
                         </p>
                    )}

                    {((item.deposit_amount || 0) > 0 || item.delivery_available) && (
                         <div className='flex flex-wrap gap-x-4 gap-y-1 mt-3'>
                              {(item.deposit_amount || 0) > 0 && (
                                   <p className='text-darker/50 text-xs'>
                                        Deposit: ${(item.deposit_amount / 100).toLocaleString()}
                                   </p>
                              )}
                              {item.delivery_available && (
                                   <p className='text-green-600 text-xs font-semibold'>
                                        Delivery: ${(item.delivery_fee / 100).toLocaleString()}
                                   </p>
                              )}
                         </div>
                    )}

                    {/* Quantity / Add controls */}
                    <div className='mt-4 pt-4 border-t border-gray-100'>
                         {selected ? (
                              <div className='flex items-center justify-between'>
                                   <span className='text-darker/60 text-sm'>
                                        In cart
                                   </span>
                                   <div className='flex items-center gap-2'>
                                        <button
                                             type='button'
                                             onClick={() => onQtyChange(item.id, maxQty, qty - 1)}
                                             className='w-9 h-9 rounded-lg border border-gray-200 text-darker hover:border-yellow duration-300 flex items-center justify-center text-lg font-bold'
                                             aria-label='Decrease quantity'
                                        >
                                             −
                                        </button>
                                        <span className='w-8 text-center font-bold text-darker'>
                                             {qty}
                                        </span>
                                        <button
                                             type='button'
                                             onClick={() => onQtyChange(item.id, maxQty, qty + 1)}
                                             disabled={qty >= maxQty}
                                             className='w-9 h-9 rounded-lg border border-gray-200 text-darker hover:border-yellow duration-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-lg font-bold'
                                             aria-label='Increase quantity'
                                        >
                                             +
                                        </button>
                                   </div>
                              </div>
                         ) : (
                              <button
                                   type='button'
                                   onClick={() => onQtyChange(item.id, maxQty, 1)}
                                   className='w-full py-3 rounded-lg border-2 border-dark text-dark text-sm font-semibold uppercase tracking-wider hover:bg-dark hover:text-white duration-300'
                              >
                                   + Add to Rental
                              </button>
                         )}
                    </div>
               </div>
          </div>
     );
}
